import React, { Suspense, useCallback, useMemo } from "react";
import { useLazyLoadQuery, usePaginationFragment } from "react-relay/hooks";

import { Button, SelectFilter, usePopoverInfiniteScroll } from "@skydio/rivet-ui";

import { useConnectionNodes } from "components/common/Pagination";
import { useSelector } from "hooks/redux";
import { useSearchParam } from "hooks/searchParams";
import { getOrgUuid, isUserInInternalOrg } from "state/current_user/selectors";
import { formatUserName } from "utils/common";
import {
  GENERIC_SKYDIO_UNKNOWN_VEHICLE_USER_EMAIL,
  GENERIC_SKYDIO_UNKNOWN_VEHICLE_USER_ID,
  GENERIC_SKYDIO_UNKNOWN_VEHICLE_USER_UUID,
} from "utils/constants";
import { MIN_DATE_FILTER } from "utils/date";

import { query, userFragment } from "./PilotFilter";

import type { FC } from "react";
import type { PilotFilter_organization$key } from "__generated__/PilotFilter_organization.graphql";
import type { PilotFilterQuery } from "__generated__/PilotFilterQuery.graphql";
import type { BaseProps } from "./SelectFilter";

interface Props extends BaseProps {
  onSelect?: () => void;
  label?: string;
  onlyShowPilots?: boolean;
  showAutonomousPilot?: boolean;
}

const PAGE_SIZE = 100;

const PilotFilter: FC<Props> = ({
  onSelect,
  className,
  style,
  label,
  placeholder,
  paramKey,
  onlyShowPilots = false,
  showAutonomousPilot = false,
}) => {
  const orgUuid = useSelector(getOrgUuid);
  const isInInternalOrg = useSelector(isUserInInternalOrg);
  const data = useLazyLoadQuery<PilotFilterQuery>(query, {
    orgUuid,
    count: PAGE_SIZE,
    hasFlightAfterDate: onlyShowPilots ? MIN_DATE_FILTER : undefined,
  });

  const paginationQuery = usePaginationFragment<PilotFilterQuery, PilotFilter_organization$key>(
    userFragment,
    data.organization
  );

  // NOTE (david.taylor): Currently we use infinite scroll for the pilot filter, which will make the
  // component more performant, since it will by default only load pilots as needed. However, this means
  // the search filter might fail to find users that haven't been loaded yet (i.e. they're on a future page).
  // A hack solution is to zip through all pages on mount (what we did before), but this will cause
  // the the componenet to load all pilots & be slower. If we want to revert to the previous behavior,
  // we can uncomment the useEffect below & remove the loaderProps from the SelectFilter component.

  // useEffect(() => {
  //   if (paginationQuery.hasNext) {
  //     paginationQuery.loadNext(PAGE_SIZE);
  //   }
  // }, [paginationQuery]);

  // stub for unknown vehicle pilot states (e.g Beacon Flight, Scheduled Flight, Pilot Undetermined);
  // will appear at the top of the select filter options
  const genericVehicleUserStub = {
    id: GENERIC_SKYDIO_UNKNOWN_VEHICLE_USER_ID,
    uuid: GENERIC_SKYDIO_UNKNOWN_VEHICLE_USER_UUID,
    displayName: GENERIC_SKYDIO_UNKNOWN_VEHICLE_USER_EMAIL,
    email: GENERIC_SKYDIO_UNKNOWN_VEHICLE_USER_EMAIL,
  };

  const nodes = useConnectionNodes(paginationQuery?.data?.users);
  // Only expose autonomous pilot user in internal orgs, since the stub user may become invalid in the future
  // Eventually this should be replaced by a more dynamic solution
  const users = showAutonomousPilot && isInInternalOrg ? [genericVehicleUserStub, ...nodes] : nodes;

  const onLoadMore = useCallback(() => {
    paginationQuery.loadNext(PAGE_SIZE);
  }, [paginationQuery]);

  const loaderProps = usePopoverInfiniteScroll({
    hasMore: paginationQuery.hasNext,
    onLoadMore,
  });

  const [selectedUserId, setSelectedUserId] = useSearchParam(paramKey);

  const handleSelectionChange = useCallback(
    (key: React.Key | null) => {
      setSelectedUserId(key as string);
      onSelect?.();
    },
    [setSelectedUserId, onSelect]
  );

  const suspenseFallback = useMemo(() => <Button.Skeleton />, []);

  return (
    <Suspense fallback={suspenseFallback}>
      <SelectFilter
        filterLabel={label ?? "Pilot"}
        selectedKey={selectedUserId}
        placeholder={placeholder ?? "Search Pilots"}
        style={style}
        className={className}
        onSingleSelectionChange={handleSelectionChange}
        {...loaderProps}
        defaultItems={users}
      >
        {item => (
          <SelectFilter.Item key={item.uuid}>
            {item.email === GENERIC_SKYDIO_UNKNOWN_VEHICLE_USER_EMAIL ? (
              <span className="italic text-gray-400">Autonomous Pilot</span>
            ) : (
              formatUserName(item)
            )}
          </SelectFilter.Item>
        )}
      </SelectFilter>
    </Suspense>
  );
};

export default PilotFilter;
